/** Catalogue reference data shared by API and web. */

export const UNITS = {
  each: { label: 'Each', short: 'ea', decimals: false },
  pack: { label: 'Pack', short: 'pk', decimals: false },
  box: { label: 'Box', short: 'box', decimals: false },
  set: { label: 'Set', short: 'set', decimals: false },
  pair: { label: 'Pair', short: 'pr', decimals: false },
  kg: { label: 'Kilogram', short: 'kg', decimals: true },
  l: { label: 'Litre', short: 'L', decimals: true },
  m: { label: 'Metre', short: 'm', decimals: true },
} as const;
export type UnitKey = keyof typeof UNITS;
export const UNIT_KEYS = Object.keys(UNITS) as UnitKey[];

/** Whole-unit products must be sold in multiples of 1000 milli-units. */
export function unitAllowsDecimals(unit: string): boolean {
  return (UNITS as Record<string, { decimals: boolean }>)[unit]?.decimals ?? false;
}

export const VAT_CATEGORIES = {
  standard: 'Standard rate',
  zero: 'Zero-rated',
  exempt: 'Exempt',
} as const;
export type VatCategory = keyof typeof VAT_CATEGORIES;
export const VAT_CATEGORY_KEYS = Object.keys(VAT_CATEGORIES) as VatCategory[];

/** Effective VAT rate for a product line. Non-registered businesses charge no VAT. */
export function effectiveVatRateBp(vatRegistered: boolean, category: VatCategory, standardRateBp: number): number {
  if (!vatRegistered) return 0;
  return category === 'standard' ? standardRateBp : 0;
}

export const PAYMENT_METHODS = {
  cash: { label: 'Cash', kind: 'cash', needsReference: false },
  card: { label: 'Card (Zimswitch / Visa / Mastercard)', kind: 'card', needsReference: false },
  ecocash: { label: 'EcoCash', kind: 'mobile', needsReference: true },
  onemoney: { label: 'OneMoney', kind: 'mobile', needsReference: true },
  innbucks: { label: 'InnBucks', kind: 'mobile', needsReference: true },
  omari: { label: "O'mari", kind: 'mobile', needsReference: true },
  bank_transfer: { label: 'Bank transfer', kind: 'bank', needsReference: true },
  account: { label: 'Customer account (credit)', kind: 'account', needsReference: false },
  deposit: { label: 'Order deposit', kind: 'deposit', needsReference: false },
} as const;
export type PaymentMethod = keyof typeof PAYMENT_METHODS;
export const PAYMENT_METHOD_KEYS = Object.keys(PAYMENT_METHODS) as PaymentMethod[];
/** Methods a cashier can choose at the till (deposit is applied automatically from orders). */
export const TENDER_METHODS: PaymentMethod[] = ['cash', 'card', 'ecocash', 'onemoney', 'innbucks', 'omari', 'bank_transfer', 'account'];

export const ADJUSTMENT_REASONS = {
  opening_stock: 'Opening stock',
  correction: 'Count correction',
  damaged: 'Damaged',
  theft: 'Theft or loss',
  found: 'Stock found',
  internal_use: 'Used in the business',
  supplier_return: 'Returned to supplier',
  expired: 'Expired',
} as const;
export type AdjustmentReason = keyof typeof ADJUSTMENT_REASONS;
export const ADJUSTMENT_REASON_KEYS = Object.keys(ADJUSTMENT_REASONS) as AdjustmentReason[];

export const MOVEMENT_KINDS = {
  receipt: 'Received',
  sale: 'Sold',
  return: 'Customer return',
  void: 'Sale voided',
  adjustment: 'Adjustment',
  transfer_out: 'Transferred out',
  transfer_in: 'Transferred in',
  stocktake: 'Stocktake',
  wastage: 'Wastage',
  breakdown_in: 'Carcass breakdown (cut produced)',
  breakdown_out: 'Carcass breakdown (carcass used)',
  order_fulfilment: 'Order fulfilled',
} as const;
export type MovementKind = keyof typeof MOVEMENT_KINDS;

/** EAN-13 / EAN-8 / UPC-A check digit validation. */
export function isValidGtin(code: string): boolean {
  if (!/^\d{8}$|^\d{12,14}$/.test(code)) return false;
  const digits = code.split('').map(Number);
  const check = digits.pop()!;
  const sum = digits.reverse().reduce((acc, d, i) => acc + d * (i % 2 === 0 ? 3 : 1), 0);
  return (10 - (sum % 10)) % 10 === check;
}

export function gtinCheckDigit(body: string): string {
  const sum = body
    .split('')
    .map(Number)
    .reverse()
    .reduce((acc, d, i) => acc + d * (i % 2 === 0 ? 3 : 1), 0);
  return String((10 - (sum % 10)) % 10);
}
